import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isAdminMode = params.get("admin") === "1";
  const [mode, setMode] = useState(params.get("tab") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register(form);
      toast.success(mode === "login" ? "Bienvenue !" : "Compte créé avec succès");
      nav(u.role === "admin" || u.role === "coach" ? "/admin" : "/mon-espace");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--sand)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="block font-serif-display text-2xl font-semibold text-center mb-8" style={{ color: "var(--pine)" }}>
          Nordic <span style={{ color: "var(--terracotta)" }}>Yoga Belgium</span>
        </Link>
        <div className="card-warm p-8">
          {isAdminMode && (
            <div className="mb-6 p-4 rounded-xl bg-[var(--forest-light)] border border-[var(--forest)]/20 flex items-center gap-3" data-testid="admin-login-banner">
              <ShieldCheck size={20} className="text-[var(--forest)]"/>
              <div>
                <div className="text-sm font-semibold" style={{ color: "var(--pine)" }}>Espace administrateur / coach</div>
                <div className="text-xs text-[var(--muted-text)]">Connectez-vous avec vos identifiants d'équipe.</div>
              </div>
            </div>
          )}
          {!isAdminMode && (
            <div className="flex gap-2 mb-6 p-1 bg-slate-100 rounded-full">
              <button onClick={() => setMode("login")} data-testid="auth-tab-login"
                className={`flex-1 py-2 rounded-full text-sm font-medium transition ${mode === "login" ? "bg-white text-[var(--pine)] shadow-sm" : "text-[var(--muted-text)]"}`}>
                Connexion
              </button>
              <button onClick={() => setMode("register")} data-testid="auth-tab-register"
                className={`flex-1 py-2 rounded-full text-sm font-medium transition ${mode === "register" ? "bg-white text-[var(--pine)] shadow-sm" : "text-[var(--muted-text)]"}`}>
                Inscription
              </button>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && !isAdminMode && (
              <>
                <input required placeholder="Prénom & nom" className="input-field" value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })} data-testid="auth-name" />
                <input placeholder="Téléphone (optionnel)" className="input-field" value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="auth-phone" />
              </>
            )}
            <input required type="email" placeholder="Email" className="input-field" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })} data-testid="auth-email" />
            <input required type="password" placeholder="Mot de passe" className="input-field" value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })} data-testid="auth-password" />
            {err && <div className="text-red-600 text-sm" data-testid="auth-error">{err}</div>}
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="auth-submit">
              {loading ? "…" : isAdminMode ? "Accéder à l'espace admin" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>
          <div className="mt-4 text-sm text-center space-y-2">
            <div><Link to="/forgot-password" className="text-[var(--forest)] hover:underline" data-testid="auth-forgot">Mot de passe oublié ?</Link></div>
            {isAdminMode ? (
              <div><Link to="/login" className="text-xs text-[var(--muted-text)] hover:text-[var(--pine)]" data-testid="switch-participant-login">← Espace participant</Link></div>
            ) : (
              <div><Link to="/login?admin=1" className="inline-flex items-center gap-1 text-xs text-[var(--muted-text)] hover:text-[var(--pine)]" data-testid="switch-admin-login"><ShieldCheck size={12}/> Espace admin / coach</Link></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
